import { ComponentRef, Injectable, inject } from '@angular/core';
import { assert } from '../../../../core/assert/assert';
import { TodoEntryModel } from '../../../../core/models/todo-planner/todo-entry.model';
import { DateTimeHelper } from '../../../calendar/util/date-time-helper';
import { TodoContainerDirective } from '../../directives/todo-container.directive';
import { TodoPlannerService } from '../../services/todo-planner.service';
import { TodoEntryComponent } from '../todo-entry.component';
import { TodoEntryService } from './todo-entry.service';

@Injectable({
  providedIn: 'root',
})
export class TodoEntryComponentHandlerService {
  // DI
  private readonly todoPlannerService = inject(TodoPlannerService);
  private readonly todoEntryService = inject(TodoEntryService);

  public buildComponent(
    todoEntryModel: TodoEntryModel,
    index?: number,
    detectChanges?: boolean,
  ): ComponentRef<TodoEntryComponent> | null {
    const container = this.queryContainer(todoEntryModel);

    // If to-do is completely outside the planner, container is null so don't add and render it at all.
    if (container) {
      const clonedTodoEntryModel = TodoEntryService.cloneTodoModel(todoEntryModel);
      const componentRef = container.vcr.createComponent(TodoEntryComponent, { index });
      this.passPlannerEventParams(componentRef, clonedTodoEntryModel);

      clonedTodoEntryModel.componentRef = componentRef;
      if (detectChanges) {
        componentRef.changeDetectorRef.detectChanges();
      }

      return componentRef;
    }
    return null;
  }

  public removeComponent(todoEntryComponent: ComponentRef<TodoEntryComponent>): void {
    assert(
      todoEntryComponent !== null && typeof todoEntryComponent !== 'undefined',
      'Component ref is invalid.',
    );

    todoEntryComponent!.destroy();
  }

  public calculateTodoEntryFixIntervalWidth(todoEntryModel: TodoEntryModel): number {
    const plannerModel = this.todoPlannerService.todoPlannerModel;
    assert(plannerModel.containerWidth !== null, 'Slot width is null.');

    const start = new Date(this.todoEntryService.getCorrectContainerDate(todoEntryModel.start));

    const duration = DateTimeHelper.eachDayOfInterval(start, todoEntryModel.end).length;
    assert(duration >= 1, `A planner event must be at least one day long but was: ${duration}`);

    return plannerModel.containerWidth * duration;
  }

  private passPlannerEventParams(
    componentRef: ComponentRef<TodoEntryComponent>,
    updatedTodoEntryModel: TodoEntryModel,
  ): void {
    componentRef.setInput('todoEntryModel', updatedTodoEntryModel);

    if (updatedTodoEntryModel.fixInterval) {
      componentRef.setInput(
        'todoEntryWidth',
        this.calculateTodoEntryFixIntervalWidth(updatedTodoEntryModel),
      );
    }
  }

  private queryContainer(todoEntryModel: TodoEntryModel): TodoContainerDirective | null {
    const todoPlannerModel = this.todoPlannerService.todoPlannerModel;
    assert(
      todoPlannerModel.fixIntervalTodoContainer !== null &&
        todoPlannerModel.variableIntervalTodoContainer !== null,
      'PlannerEventSlots are null.',
    );

    let todoContainer: TodoContainerDirective[];

    if (todoEntryModel.fixInterval) {
      todoContainer = todoPlannerModel.fixIntervalTodoContainer;
    } else {
      todoContainer = todoPlannerModel.variableIntervalTodoContainer;
    }

    if (this.todoEntryService.isOutsideOfView(todoEntryModel)) {
      return null;
    }

    const startDate = DateTimeHelper.format(
      this.todoEntryService.getCorrectContainerDate(todoEntryModel.start),
    );
    return (todoContainer as TodoContainerDirective[]).find(
      (c) => DateTimeHelper.format(c.date()) === startDate,
    );
  }
}
